import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { isGated } from "~/lib/membership";
import { reminderSettings, ROUTINE_SLOTS } from "~/lib/routine";
import {
  type RoutineReminder,
  syncRoutineReminders,
} from "~/lib/routine-reminders";
import { useAuthStore } from "~/store/auth-store";
import type { RoutineSlot } from "~/types/routine";

/**
 * Keeps this device's reminder notifications in step with the account. Renders nothing.
 *
 * Mounted once, beside `NativeShell` and outside every gate, because it has to follow
 * the account wherever the member is — including onto the paywall. It re-plans when the
 * member's reminder settings, their access or the app's language change; the plan is
 * compared with what the device already holds, so an unrelated re-render costs nothing.
 */
export function RoutineReminderSync() {
  const { t, i18n } = useTranslation();
  const status = useAuthStore((state) => state.status);
  const user = useAuthStore((state) => state.user);

  // A lapsed member lands on the paywall, where the switch that turns these off cannot
  // be reached — so the reminders go quiet with the access, and return with it.
  const entitled = user !== null && !isGated(user);
  const settings = user ? reminderSettings(user) : null;
  const morning =
    entitled && settings?.morning.enabled ? settings.morning.time : null;
  const evening =
    entitled && settings?.evening.enabled ? settings.evening.time : null;
  const language = i18n.language;

  useEffect(() => {
    // Only a confirmed session decides. "loading" is a boot still in progress, and a
    // boot that failed offline reads "unauthenticated" while keeping its token —
    // neither is a reason to silence a reminder the member set. Signing out cancels
    // explicitly, in the auth store.
    if (status !== "authenticated") return;
    const times: Record<RoutineSlot, string | null> = { morning, evening };
    const reminders: RoutineReminder[] = ROUTINE_SLOTS.flatMap((slot) => {
      const time = times[slot];
      return time
        ? [
            {
              slot,
              time,
              title: t("routine.reminder.title"),
              body: t(`routine.reminder.${slot}`),
            },
          ]
        : [];
    });
    void syncRoutineReminders({
      reminders,
      channel: {
        name: t("routine.reminder.channel"),
        description: t("routine.reminder.channelDescription"),
      },
    }).catch(() => undefined);
  }, [status, morning, evening, language, t]);

  return null;
}
